import React from "react";
import { Button } from "@/components/ui/button";
import { Star, Facebook, ShieldCheck, Users, AlertTriangle, HeartHandshake, ClipboardCheck, ShoppingBag } from "lucide-react";

const LEARN = [
  { icon: ShieldCheck, title: "Lead with confidence", desc: "Understand the Oral Champion role and how to support colleagues." },
  { icon: HeartHandshake, title: "Improve everyday mouth care", desc: "Practical approaches to delivering effective oral healthcare." },
  { icon: AlertTriangle, title: "Recognise problems early", desc: "Understand the impact of poor oral health and what to look out for." },
  { icon: Users, title: "Support people who resist care", desc: "Practical strategies for care-resistant behaviour." },
  { icon: ClipboardCheck, title: "Assessments & standards", desc: "Build confidence with oral health assessments and quality standards." },
  { icon: ShoppingBag, title: "Know what to recommend", desc: "Appropriate mouth-care products, including palliative and end-of-life care." },
];

const TESTIMONIALS = [
  { quote: "Very enlightening. Packed with so much practical knowledge, insight and real-life application that I could begin using immediately. Well structured, engaging and easy to follow, yet rich in depth and value.", name: "C. Amanze", role: "Carer · Google Review" },
  { quote: "The experience was excellent. Very well organised and the training was extremely good — very clear communication and making sure we understood everything and the skills to improve oral hygiene in care homes.", name: "C. Agate", role: "Carer · Google Review" },
  { quote: "I've recently become an oral health care champion. This course put everything in perspective, identified my role and guided me through the knowledge and responsibilities I needed. I'd recommend it to anybody exploring this role.", name: "Care Assistant", role: "West Acre Nursing Home, Winchester" },
  { quote: "Extremely useful information, would recommend attending. You have the opportunity to ask questions and advice which helps.", name: "Deputy Service Manager", role: "Action 4 Care" },
];

export function LearnGrid() {
  return (
    <section className="max-w-7xl mx-auto px-5 md:px-8 py-10">
      <h2 className="text-3xl md:text-4xl font-bold text-brand tracking-tight mb-10">What will your Oral Champion learn?</h2>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {LEARN.map((l) => (
          <div key={l.title} className="bg-cream-card border border-cream-deep rounded-2xl p-7">
            <div className="h-11 w-11 rounded-xl bg-brand-light grid place-items-center mb-5"><l.icon className="h-5 w-5 text-brand" /></div>
            <h3 className="text-lg font-bold text-brand">{l.title}</h3>
            <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{l.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ChampionTestimonials() {
  return (
    <section className="bg-cream-deep/40 py-16">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-brand tracking-tight mb-10">What champions say</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} data-testid={`oc-testimonial-${i}`} className="bg-cream-card border border-cream-deep rounded-2xl p-7">
              <div className="flex gap-1 mb-4">{Array.from({ length: 5 }).map((_, k) => <Star key={`star-${k}`} className="h-4 w-4 fill-coral text-coral" />)}</div>
              <p className="text-foreground leading-relaxed">“{t.quote}”</p>
              <div className="mt-5 pt-5 border-t border-cream-deep"><div className="font-semibold text-brand">{t.name}</div><div className="text-sm text-muted-foreground">{t.role}</div></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CommunityHubBanner() {
  return (
    <section className="max-w-7xl mx-auto px-5 md:px-8 pb-4">
      <div className="rounded-2xl bg-brand text-white p-8 md:p-10 flex flex-wrap items-center justify-between gap-6">
        <div className="max-w-2xl"><span className="label-caps text-coral">Oral Champion Community Hub</span>
          <h3 className="text-2xl md:text-3xl font-bold mt-2">Every Oral Champion is invited</h3>
          <p className="text-white/70 mt-2">All Oral Champions are invited to join our Community Hub for questions and discussion — open to everyone who has completed any of our training courses.</p></div>
        <a href="https://www.facebook.com/groups/27941660172109456" target="_blank" rel="noreferrer"><Button data-testid="oc-community-btn" className="h-12 px-8 rounded-full bg-coral hover:bg-coral-hover text-white"><Facebook className="h-4 w-4 mr-2" />Join the Community Hub</Button></a>
      </div>
    </section>
  );
}
